/**
 * Canonical prompt templates. Strings may contain the literal "this project"
 * which `SuggestionChips` will substitute with the active project name.
 */
export const SUGGESTIONS: readonly {
  label: string;
  prompt: string;
  icon:
    | "architecture"
    | "bom"
    | "power"
    | "mcu"
    | "bus"
    | "drc"
    | "thermal"
    | "protection";
}[] = [
  {
    label: "System architecture",
    prompt:
      "Show the system architecture and functional blocks of this project. Render a Mermaid block diagram of the major subsystems (power, MCU, sensors, comms, protection) and the interfaces between them, then call out anything unusual or load-bearing in the topology.",
    icon: "architecture",
  },
  {
    label: "Generate a BOM",
    prompt:
      "Generate a complete BOM for this project as a markdown table. Include reference designators, value, manufacturer part number, LCSC number, package, and quantity, grouped by component class. Flag any parts missing an MPN or LCSC number.",
    icon: "bom",
  },
  {
    label: "Power tree",
    prompt:
      "Show the full power tree for this project: input source, every voltage rail, the regulator that generates each rail, the load groups it feeds, decoupling per rail, and any voltage-domain crossings. Render it as a Mermaid diagram and flag rails with missing or insufficient decoupling.",
    icon: "power",
  },
  {
    label: "MCU peripherals",
    prompt:
      "Find the MCU on this project and list its peripheral connections grouped by protocol (SPI, I²C, UART, CAN, USB, ADC, GPIO). For each peripheral show the pin number, net name, attached device, and any pull-ups or termination. Note any GPIOs that look unused or unrouted.",
    icon: "mcu",
  },
  {
    label: "Detect buses",
    prompt:
      "Detect all buses on this project (SPI, I²C, UART, CAN, SWD, JTAG, ADC). For each bus list the members, identify the master, list pull-ups and termination, and infer the bus speed where you can. Flag any bus missing pull-ups, missing termination, or with mismatched logic levels.",
    icon: "bus",
  },
  {
    label: "Run DRC",
    prompt:
      "Run a full DRC on this project. Check: power decoupling per IC, differential-pair and serial-bus termination, dangling or orphan nets, floating components, high-fanout nets (≥10 pins), and parallel-component groups. Return a prioritized list of issues with severity (critical / serious / moderate) and the affected reference designators.",
    icon: "drc",
  },
  {
    label: "Thermal audit",
    prompt:
      "Run a thermal audit on this project. For every IC and power component, look up θ_ja from the datasheet, estimate worst-case power dissipation, compute the resulting junction temperature, and rank parts by thermal risk (critical / serious / moderate). Highlight anything operating within 20 °C of its absolute-max junction temperature.",
    icon: "thermal",
  },
  {
    label: "Protection circuits",
    prompt:
      "Identify all protection circuits on this project: TVS and ESD diodes on connectors and exposed I/O, fuses, polarity-reversal protection, over-voltage clamps, and inrush limiting. Show protection coverage as a table per port and per rail, and flag any external-facing connector or rail without protection.",
    icon: "protection",
  },
];

// Board uploads are modelled around the source EDA tool + file slots —
// see `@features/upload` (`SOURCE_TOOLS`). The old flat `NETLIST_FORMATS`
// list, `FORMAT_TYPE_BACKEND` map, and `FORMAT_HINTS` were retired with
// the tool-based upload revamp.

export const MERMAID_STARTERS = [
  "graph",
  "flowchart",
  "sequenceDiagram",
  "classDiagram",
  "stateDiagram",
  "erDiagram",
  "gantt",
  "pie",
  "gitGraph",
] as const;
